import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { useScanner, type ScanSource, type UseScannerOptions } from './useScanner';

export type ScanStatus = 'off' | 'waiting' | 'active' | 'last';

export type ScanCaptureHandle = { readonly focus: () => void };

type Props = Pick<UseScannerOptions, 'config' | 'timeOf' | 'now'> & {
  readonly enabled: boolean;
  /** `active` while a scan is being processed. */
  readonly busy: boolean;
  readonly lastCode: string | null;
  readonly onScan: (code: string, source: ScanSource) => void;
  readonly onDuplicate?: (code: string) => void;
};

export function scanStatus(enabled: boolean, busy: boolean, lastCode: string | null): ScanStatus {
  if (!enabled) return 'off';
  if (busy) return 'active';
  return lastCode ? 'last' : 'waiting';
}

/**
 * The scan engine's visible half: global wedge capture (`useScanner`) plus an always-ready scan field, which is
 * also the manual entry path - typing a code and pressing Enter is exactly a scan. After every scan the field
 * takes focus back. «Ввести вручную» opens the on-screen keyboard on a phone (the field is `inputMode="none"`
 * otherwise, so a Bluetooth scanner does not make the keyboard pop up).
 */
export const ScanCapture = forwardRef<ScanCaptureHandle, Props>(function ScanCapture(
  { enabled, busy, lastCode, onScan, onDuplicate, config, timeOf, now }, ref,
): JSX.Element | null {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [manual, setManual] = useState(false);
  const { submit } = useScanner({ enabled, onScan, onDuplicate, config, timeOf, now });
  const status = scanStatus(enabled, busy, lastCode);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }), []);
  useEffect(() => {
    if (enabled) inputRef.current?.focus();
  }, [enabled]);

  if (!enabled) return null;

  function accept(): void {
    const code = value;
    setValue('');
    submit(code, 'field');
    inputRef.current?.focus();
  }
  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    accept();
  }
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    // A scanner configured with a Tab suffix ends a scan with Tab: keep the focus here.
    if (e.key === 'Tab' && !e.shiftKey && value.trim() !== '') {
      e.preventDefault();
      accept();
    }
  }

  return (
    <div className="scan-capture" data-testid="scan-capture">
      <div className="scan-status-row">
        <span className={`scan-chip scan-chip--${status}`} data-testid="scan-status" data-status={status}>
          {status === 'last' && lastCode ? t('scan.status.last', { code: lastCode }) : t(`scan.status.${status}` as const, { code: '' })}
        </span>
      </div>
      <form className="scan-field" onSubmit={onSubmit} autoComplete="off">
        <label htmlFor="scan-input" className="scan-field-label">{t('scan.field')}</label>
        <div className="scan-field-row">
          <input
            id="scan-input"
            ref={inputRef}
            data-scan-field="true"
            data-testid="scan-input"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            inputMode={manual ? 'text' : 'none'}
            enterKeyHint="done"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t('scan.field.placeholder')}
          />
          <button type="submit" className="primary" disabled={value.trim() === ''}>{t('scan.submit')}</button>
        </div>
        <button
          type="button"
          className="link-button scan-manual"
          aria-pressed={manual}
          onClick={() => {
            setManual((m) => !m);
            inputRef.current?.focus();
          }}
        >
          {manual ? t('scan.manualOff') : t('scan.manual')}
        </button>
      </form>
    </div>
  );
});
